import React, { useMemo, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const QualityTimeline = ({ data = [] }) => {
    useEffect(() => {
        console.log('🔍 Timeline mounted/updated');
        console.log('📊 Raw data received:', data);
        console.log('📊 Data length:', data?.length);
        if (data && data.length > 0) {
            console.log('📊 First task sample:', data[0]);
        }
    }, [data]);

    // Process data to aggregate by date
    const chartData = useMemo(() => {
        if (!data || data.length === 0) {
            console.log('⚠️ Timeline - No data available');
            return [];
        }

        const acc = {};
        let tasksWithDate = 0;
        let tasksWithoutDate = 0;

        data.forEach(task => {
            if (!task.due_date) {
                tasksWithoutDate++;
                return;
            }

            tasksWithDate++;
            const date = new Date(task.due_date);
            const dateStr = date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });

            if (!acc[dateStr]) {
                acc[dateStr] = {
                    date: dateStr,
                    Pendiente: 0,
                    "En Progreso": 0,
                    Revisión: 0,
                    Completado: 0,
                    Fallos: 0,
                    _sortKey: date.getTime()
                };
            }

            const statusKey = task.status || 'Pendiente';
            if (acc[dateStr][statusKey] !== undefined) {
                acc[dateStr][statusKey]++;
            } else {
                console.warn(`⚠️ Unknown status: ${statusKey} for task:`, task.title);
            }
        });

        console.log(`📊 Tasks with date: ${tasksWithDate}, without date: ${tasksWithoutDate}`);

        const result = Object.values(acc).sort((a, b) => a._sortKey - b._sortKey);
        result.forEach(item => delete item._sortKey);

        console.log('📊 Processed chart data:', result);
        console.log(`📊 Chart will show ${result.length} date points`);

        return result;
    }, [data]);

    if (!data || data.length === 0) {
        return (
            <div style={{
                padding: '40px',
                background: '#ffffff',
                minHeight: '500px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
            }}>
                <div style={{
                    fontSize: '48px',
                    marginBottom: '16px',
                    opacity: 0.3
                }}>📊</div>
                <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    margin: 0,
                    fontWeight: 600
                }}>
                    No hay tareas disponibles
                </p>
                <p style={{
                    color: '#94a3b8',
                    fontSize: '12px',
                    marginTop: '8px',
                    textAlign: 'center',
                    maxWidth: '300px'
                }}>
                    Crea tareas con fechas de vencimiento para ver la distribución temporal en este gráfico.
                </p>
            </div>
        );
    }

    if (chartData.length === 0) {
        return (
            <div style={{
                padding: '40px',
                background: '#ffffff',
                minHeight: '500px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
            }}>
                <div style={{
                    fontSize: '48px',
                    marginBottom: '16px',
                    opacity: 0.3
                }}>📅</div>
                <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    margin: 0,
                    fontWeight: 600
                }}>
                    Las tareas no tienen fechas de vencimiento
                </p>
                <p style={{
                    color: '#94a3b8',
                    fontSize: '12px',
                    marginTop: '8px',
                    textAlign: 'center',
                    maxWidth: '300px'
                }}>
                    Edita las tareas existentes para agregar fechas de vencimiento y visualizar la distribución.
                </p>
                <p style={{
                    color: '#cbd5e1',
                    fontSize: '11px',
                    marginTop: '12px',
                    fontFamily: 'monospace'
                }}>
                    {data.length} tareas sin fecha
                </p>
            </div>
        );
    }

    return (
        <div style={{
            padding: '24px',
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                <h3 style={{
                    margin: 0,
                    color: '#0c2c6c',
                    fontSize: '16px',
                    fontWeight: 700
                }}>
                    Distribución de Tareas por Fecha
                </h3>
                <span style={{
                    fontSize: '12px',
                    color: '#64748b',
                    background: '#f1f5f9',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontWeight: 600
                }}>
                    {chartData.length} fechas • {data.length} tareas
                </span>
            </div>

            <div style={{ height: '450px', width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis
                            dataKey="date"
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                            style={{ fontWeight: 500 }}
                        />
                        <YAxis
                            stroke="#64748b"
                            fontSize={11}
                            tickLine={false}
                            axisLine={false}
                            allowDecimals={false}
                            style={{ fontWeight: 500 }}
                        />
                        <Tooltip
                            cursor={{ fill: '#f1f5f9' }}
                            contentStyle={{
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                padding: '8px 12px',
                                fontSize: '12px'
                            }}
                        />
                        <Legend
                            wrapperStyle={{ paddingTop: '16px', fontSize: '12px' }}
                            iconType="circle"
                            iconSize={8}
                        />
                        <Bar dataKey="Pendiente" name="Pendientes" fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={24} stackId="a" />
                        <Bar dataKey="En Progreso" name="En Progreso" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={24} stackId="a" />
                        <Bar dataKey="Revisión" name="Revisión" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={24} stackId="a" />
                        <Bar dataKey="Completado" name="Completados" fill="#10b981" radius={[4, 4, 0, 0]} barSize={24} stackId="a" />
                        <Bar dataKey="Fallos" name="Fallos" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={24} stackId="a" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default QualityTimeline;
